import { type FunctionComponent, type KeyboardEvent, type MouseEvent, useCallback } from "react";
import cn from "classnames";
import { isApiUrl, openDocument } from "../../utils/document-url.utils";

interface ReferenceDocPopoverProps {
  children: React.ReactNode;
  href?: string;
}

export const ReferenceDocPopover: FunctionComponent<ReferenceDocPopoverProps> = ({ children, href }) => {
  const hasExternalLink: boolean = !!href && !href?.startsWith("#");
  const requiresAuth = hasExternalLink && isApiUrl(href);

  const navigateToLink = useCallback(() => {
    if (!href || !hasExternalLink) return;

    if (requiresAuth) {
      openDocument(href);
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  }, [href, hasExternalLink, requiresAuth]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
      navigateToLink();
    },
    [navigateToLink]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        navigateToLink();
      }
    },
    [navigateToLink]
  );

  // The hover overview (`ReferenceDocSourceInfo` in a Popover) is hidden until it gets a better
  // presentation: it stayed open over the chat after the document opened in a new tab.
  return (
    <a
      href={href}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn("no-underline text-inherit", hasExternalLink ? "cursor-pointer" : "cursor-default")}
    >
      {children}
    </a>
  );
};
